import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ethers } from "ethers";
import { useAccount, useConnect, useNetwork, useSwitchNetwork, useSignMessage } from "wagmi";
import { useAuth } from "../../context/AuthContext";
import { useEthersSigner } from "../../hooks/useEthersSigner";
import {
  ensureTreydoraWalletSession,
  formatWalletSessionError,
  WALLET_SIGN_IN_HINT,
} from "../../lib/ensureTreydoraWalletSession";
import { getChainLabel, getSwitchToNetworkLabel } from "../../config/chainEnv";
import {
  getNftComicV2ChainId,
  getNftComicV2ContractAddressChecksummed,
  isNftComicV2MintUiVisible,
} from "../../services/libraryNftComicV2Env";
import {
  getTreydoraGenesisComic721V2Contract,
  readV2TokenEthPrice,
  mintWithEthV2,
} from "../../services/web3/treydoraGenesisComic721V2";
import { syncComicV2WalletMint } from "../../services/libraryApi";
import { clearPendingLibraryPurchase } from "../../utils/pendingLibraryPurchase";

function formatWeiEth(amountWei) {
  try {
    return `${ethers.utils.formatEther(amountWei)} ETH`;
  } catch {
    return "ETH";
  }
}

/**
 * V2 direct wallet mint: mintWithETH on TreydoraGenesisComic721V2 + backend sync.
 * Hidden unless REACT_APP_NFT_COMIC_V2_MINT_ENABLED and V2 env contract/chain are configured.
 * Token id is manual dev input until a catalog endpoint exists.
 */
export default function LibraryNftV2DirectMintControls({
  disabled = false,
  purchaseAllowed = true,
  /** When set, invoked instead of minting if `purchaseAllowed` is false (parent opens legal gate). */
  onLegalAcknowledgmentRequired,
  registerWalletResume,
  compact = false,
}) {
  const navigate = useNavigate();
  const { walletLogin, linkWallet } = useAuth();
  const { address, isConnected } = useAccount();
  const { connectAsync, connectors } = useConnect();
  const { chain } = useNetwork();
  const { switchNetwork } = useSwitchNetwork();
  const { signMessageAsync } = useSignMessage();
  const signer = useEthersSigner();

  const [tokenIdInput, setTokenIdInput] = useState("");
  const [priceWei, setPriceWei] = useState(null);
  const [busy, setBusy] = useState(false);
  const [hint, setHint] = useState("");
  const [err, setErr] = useState("");

  const v2Address = useMemo(() => getNftComicV2ContractAddressChecksummed(), []);
  const expectedChainId = useMemo(() => getNftComicV2ChainId(), []);
  const expectedNetworkLabel = useMemo(() => getChainLabel(expectedChainId), [expectedChainId]);

  const currentChainId = chain?.id != null ? Number(chain.id) : null;
  const wrongNetwork =
    Boolean(isConnected && address && currentChainId != null) && currentChainId !== expectedChainId;

  const loadPrice = useCallback(async () => {
    setErr("");
    setHint("");
    const tid = Math.floor(Number(String(tokenIdInput).trim()) || 0);
    if (!tid || tid < 1 || tid > 20000) {
      setErr("Enter a token id in the Genesis Comic range (1–20000).");
      setPriceWei(null);
      return;
    }
    if (!signer || !v2Address) {
      setErr(`Connect wallet on ${expectedNetworkLabel} to read price.`);
      return;
    }
    try {
      const ro = getTreydoraGenesisComic721V2Contract(signer.provider || signer, v2Address);
      const wei = await readV2TokenEthPrice(ro, tid);
      setPriceWei(wei);
      if (wei.isZero()) {
        setHint("On-chain price is zero — owner must set tokenEthPrice before mint.");
      } else {
        setHint(`On-chain price: ${formatWeiEth(wei)}`);
      }
    } catch (e) {
      setPriceWei(null);
      setErr(e?.reason || e?.message || String(e));
    }
  }, [signer, tokenIdInput, v2Address, expectedNetworkLabel]);

  const runMintAndSync = useCallback(async () => {
    setErr("");
    setHint("");
    setBusy(true);
    clearPendingLibraryPurchase();
    try {
      if (!purchaseAllowed) {
        if (typeof onLegalAcknowledgmentRequired === "function") onLegalAcknowledgmentRequired();
        else setErr("Please review purchase terms before minting.");
        return;
      }

      let activeAddress = address;
      if (!isConnected || !activeAddress) {
        const connList = Array.isArray(connectors) ? connectors : [];
        const connector = connList.find((c) => c && c.ready) || connList[0];
        if (!connector) {
          throw new Error("No wallet connector available.");
        }
        const connected = await connectAsync({ connector });
        activeAddress = connected?.account;
      }
      if (!activeAddress) throw new Error("Wallet not connected.");

      setHint(WALLET_SIGN_IN_HINT);
      await ensureTreydoraWalletSession({
        walletAddress: activeAddress,
        signMessageAsync,
        walletLogin,
        linkWallet,
      });
      setHint("");

      if (wrongNetwork || (currentChainId != null && currentChainId !== expectedChainId)) {
        if (switchNetwork) switchNetwork(expectedChainId);
        setHint(`${getSwitchToNetworkLabel(expectedChainId)}, then try Mint Directly with Wallet again.`);
        return;
      }

      if (!signer || !v2Address) {
        throw new Error("Wallet signer not ready.");
      }

      const tid = Math.floor(Number(String(tokenIdInput).trim()) || 0);
      if (!tid || tid < 1 || tid > 20000) {
        throw new Error("Enter a valid token id (1–20000).");
      }

      const ro = getTreydoraGenesisComic721V2Contract(signer.provider || signer, v2Address);
      const wei = await readV2TokenEthPrice(ro, tid);
      if (!wei || wei.isZero()) {
        throw new Error("Price not set on-chain for this token (tokenEthPrice is 0).");
      }

      const cSigned = getTreydoraGenesisComic721V2Contract(signer, v2Address);
      setHint(`Confirm mint in wallet (${formatWeiEth(wei)})…`);
      const { receipt } = await mintWithEthV2(cSigned, tid, wei);
      const txHash = receipt.transactionHash;

      setHint("Mint confirmed — syncing to My Library…");
      await syncComicV2WalletMint({ txHash, chainId: expectedChainId });

      setHint("Synced. Opening My Library…");
      setTimeout(() => navigate("/dashboard/library"), 800);
    } catch (e) {
      const raw = e?.reason || e?.message || String(e);
      if (String(raw).toLowerCase().includes("user rejected") || String(raw).toLowerCase().includes("denied")) {
        setErr("Transaction was cancelled in your wallet.");
      } else if (/rejected|denied|signature cancelled/i.test(String(raw))) {
        setErr(formatWalletSessionError(e));
      } else {
        setErr(raw || "V2 mint or sync failed.");
      }
    } finally {
      setBusy(false);
    }
  }, [
    purchaseAllowed,
    onLegalAcknowledgmentRequired,
    address,
    isConnected,
    connectors,
    connectAsync,
    signMessageAsync,
    walletLogin,
    linkWallet,
    wrongNetwork,
    currentChainId,
    switchNetwork,
    signer,
    tokenIdInput,
    v2Address,
    expectedChainId,
    navigate,
  ]);

  const runMintAndSyncRef = useRef(runMintAndSync);
  runMintAndSyncRef.current = runMintAndSync;

  useEffect(() => {
    if (typeof registerWalletResume !== "function") return undefined;
    registerWalletResume(() => runMintAndSyncRef.current());
    return () => registerWalletResume(null);
  }, [registerWalletResume]);

  const mintLabel = (() => {
    if (!isConnected || !address) return busy ? "Connecting…" : "Connect Wallet (V2)";
    if (wrongNetwork) return getSwitchToNetworkLabel(expectedChainId);
    if (busy) return "Minting / syncing…";
    return "Mint Directly with Wallet";
  })();

  if (!isNftComicV2MintUiVisible()) return null;

  return (
    <div className={`lib-nft-wallet-pay lib-nft-v2-mint${compact ? " lib-nft-wallet-pay--compact" : ""}`}>
      <p className="lib-nft-wallet-pay__note lib-nft-v2-mint__eyebrow">
        <strong>V2 ({expectedNetworkLabel})</strong> — Direct contract <code>mintWithETH</code>, then library sync. Requires this
        wallet linked on your Treydora account. Token id is manual until catalog selection ships.
      </p>
      <label className="lib-nft-v2-mint__label" htmlFor="lib-nft-v2-token-id">
        Token id (test)
      </label>
      <input
        id="lib-nft-v2-token-id"
        className="lib-nft-v2-mint__input"
        type="number"
        min={1}
        max={20000}
        step={1}
        placeholder="e.g. 10062"
        value={tokenIdInput}
        onChange={(e) => setTokenIdInput(e.target.value)}
        disabled={busy}
      />
      <div className="lib-nft-v2-mint__row">
        <button
          type="button"
          className="lib-btn lib-btn--sm lib-nft-v2-mint__secondary"
          disabled={disabled || busy}
          onClick={() => void loadPrice()}
        >
          Read on-chain price
        </button>
      </div>
      {priceWei != null && !priceWei.isZero() ? (
        <p className="lib-nft-wallet-pay__amount" aria-live="polite">
          Mint will send: <strong>{formatWeiEth(priceWei)}</strong>
        </p>
      ) : null}
      {hint ? (
        <p className="lib-nft-wallet-pay__hint" role="status">
          {hint}
        </p>
      ) : null}
      {err ? (
        <p className="lib-nft-wallet-pay__err" role="alert">
          {err}
        </p>
      ) : null}
      <button
        type="button"
        className="lib-btn lib-btn--sm lib-nft-wallet-pay__btn lib-nft-v2-mint__primary"
        disabled={disabled || busy}
        aria-busy={busy ? "true" : undefined}
        onClick={() => void runMintAndSync()}
      >
        {mintLabel}
      </button>
    </div>
  );
}
